import { useWorkspaceText } from './interface-copy';
import { useState } from 'react';
import type { ChartPayload, TablePayload } from './types';
import { NativeWorkspaceChart } from './artifacts/chart/native';
import { EnhancedWorkspaceChart } from './artifacts/chart/view';
import type { EnhancedChartOptions } from './artifacts/chart/types';
import { columnType, formatCell, isNumeric } from './artifacts/table/model';
import { rowsLabel } from './artifacts/table/copy';
import { formatNumber } from './artifacts/money.ts';
import { splitDomMetadata, type DomMetadata } from './dom-metadata';
export type { ChartEvent, ChartMark, EnhancedChartOptions } from './artifacts/chart/types';

import { chartTable } from '../../src/artifacts/tabular.mjs';
export { csvText, tableChart } from '../../src/artifacts/tabular.mjs';
export function WorkspaceTable({ table, locale }: { table: TablePayload; locale?: string }) {
  const { text: uiText, rich: uiRich, locale: interfaceLocale } = useWorkspaceText();
  // Cells use the shared cell format (money, percent, numbers), never a bare String().
  const types = table.columns.map((column) => columnType(column, table.rows));
  const [filter, setFilter] = useState('');
  const filtered = table.rows.filter((row) =>
    table.columns.some((column) =>
      String(row[column.key] ?? '')
        .toLocaleLowerCase()
        .includes(filter.toLocaleLowerCase()),
    ),
  );
  return (
    <>
      <div className="ws-table-tools" data-few={table.columns.length <= 2 ? 'true' : undefined}>
        <input
          aria-label={uiText('Filter result rows')}
          placeholder={uiText('Find a product or value…')}
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        />
        <span>
          {uiRich('{value1} of {value2}', {
            value1: formatNumber(filtered.length, interfaceLocale),
            value2: rowsLabel(table.rows.length, interfaceLocale),
          })}
        </span>
      </div>
      <div
        className="ws-table-scroll"
        tabIndex={0}
        aria-label={uiText('Result table')}
        data-few={table.columns.length <= 2 ? 'true' : undefined}
      >
        <table>
          <thead>
            <tr>
              {table.columns.map((column, i) => (
                <th
                  key={column.key}
                  scope="col"
                  data-align={isNumeric(types[i]) ? 'right' : undefined}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 1000).map((row, index) => (
              <tr key={index}>
                {table.columns.map((column, i) => (
                  <td key={column.key} data-align={isNumeric(types[i]) ? 'right' : undefined}>
                    {formatCell(row[column.key], column, types[i], locale) || '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!filtered.length && <p className="ws-muted">{uiText('No matching rows.')}</p>}
      {filtered.length > 1000 && (
        <p className="ws-muted">
          {uiText('Showing the first 1,000 matching rows. Download the CSV for every row.')}
        </p>
      )}
    </>
  );
}
/** A fallback column is labelled by its series name, else its key in sentence case ("week" → "Week"). */
export function columnLabel(
  column: { key: string; label: string },
  enhanced: EnhancedChartOptions,
) {
  const named = enhanced.series?.find((s) => s.key === column.key)?.label;
  if (named) return named;
  const words = column.label.replaceAll('_', ' ').trim();
  return words.charAt(0).toLocaleUpperCase() + words.slice(1);
}
export function WorkspaceChart({
  chart,
  columns,
  enhanced,
  locale,
  ...props
}: {
  chart: ChartPayload;
  columns?: TablePayload['columns'];
  enhanced?: EnhancedChartOptions;
  locale?: string;
} & DomMetadata) {
  const { metadata } = splitDomMetadata(props);
  if (!enhanced)
    return <NativeWorkspaceChart chart={chart} columns={columns} locale={locale} {...metadata} />;
  const table = {
    // Without declared columns, the chart's own `currency` types its plotted columns as money.
    columns:
      columns ?? chartTable(chart).columns.map((c) => ({ ...c, label: columnLabel(c, enhanced) })),
    rows: chart.rows,
  };
  return (
    <EnhancedWorkspaceChart
      {...metadata}
      chart={chart}
      columns={columns}
      enhanced={enhanced}
      table={<WorkspaceTable table={table} locale={enhanced.locale} />}
    />
  );
}
